<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">סוג השובר</span>
    <div class="flex gap-2">
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="newVoucher.voucherType === VoucherTypes.AMOUNT
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="newVoucher.voucherType = VoucherTypes.AMOUNT"
      >₪ סכום</button>
      <button
        type="button"
        class="flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors"
        :class="newVoucher.voucherType === VoucherTypes.SERVICE
          ? 'border-primary bg-primary-50 text-primary'
          : 'border-gray-200 bg-white text-gray-600'"
        @click="newVoucher.voucherType = VoucherTypes.SERVICE"
      >הנחה לשירות</button>
    </div>
    <div
      v-if="newVoucher.voucherType === VoucherTypes.AMOUNT"
      class="flex flex-col w-full gap-2">
      <span class="mt-3 mb-1 font-medium">ערך השובר</span>
      <input-number
        v-model="newVoucher.amount"
        :max="1000"
        :maxFractionDigits="0"
        placeholder="סכום השובר"
        mode="currency"
        currency="ILS"
        locale="he-IL"
      />
    </div>
    <div
      v-if="newVoucher.voucherType === VoucherTypes.SERVICE"
      class="flex flex-col w-full gap-2">
      <span class="mt-3 mb-1 font-medium">בחירת שירותים</span>
      <select-service
        v-model="selectedServices"
        @services-selected="onServicesSelected"
      />

      <settings-toggle
        class="mt-3 mb-1 font-medium"
        :status="isServiceFree ?? false"
        title="האם השובר הוא מתנה?"
        @value-changed="(val) => {isServiceFree = val}" />

      <input-number
        v-if="!isServiceFree"
        v-model="newVoucher.serviceDiscount"
        placeholder="כמה אחוז הנחה?"
        :min="1"
        :max="99"
        suffix="%" />
    </div>
    <span v-if="!!newVoucher.voucherType" class="mt-3 mb-1 font-medium">תאריך תוקף</span>
    <ResponsiveDatePicker
      v-if="!!newVoucher.voucherType"
      v-model="newVoucher.validUntilDate"
      :minDate="new Date()"
      title="תאריך תוקף" />
    <template v-if="!!newVoucher.voucherType && !isCustomerLocked">
      <span class="mt-3 mb-1 font-medium">למי לשלוח את השובר?</span>
      <SelectCustomer
        v-if="whoToSend === 'customer'"
        v-model="selectedCustomerId"
        @customer-selected="onCustomerSelected"
      />

      <span v-if="whoToSend === 'phone'" class="mt-3 mb-1 font-medium">מספר טלפון</span>
      <InputMask
        v-if="whoToSend === 'phone'"
        id="address"
        v-model="selectedPhone"
        type="tel"
        inputmode="numeric"
        mask="999-999-9999"
        pattern="[0-9]*"
      />
    </template>

    <template v-if="!!newVoucher.voucherType">
      <span class="mt-3 mb-1 font-medium">הודעה אישית <span class="text-surface-400 font-normal">(אופציונלי)</span></span>
      <Textarea
        v-model="personalMessage"
        rows="3"
        autoResize
        :maxlength="VOUCHER_PERSONAL_MESSAGE_MAX_LENGTH"
        placeholder="לדוגמה: מזל טוב ליום ההולדת! מגיע לך פינוק 💜" />
      <small class="text-surface-400">ההודעה תופיע ללקוח/ה בעמוד השובר. {{ personalMessage.length }}/{{ VOUCHER_PERSONAL_MESSAGE_MAX_LENGTH }}</small>
    </template>

    <drawer-footer
      class="mt-auto"
      :approveDisable="!validateFinished"
      :approveLoading="loadingCta"
      @approve-click="createVoucher"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { IClient, ICustomer, IService, IVoucher, VOUCHER_PERSONAL_MESSAGE_MAX_LENGTH, VoucherTypes } from '@torim-app/types';
import { useAdminClientsStore, useAdminStore } from '@app/stores';
import { DateTime } from 'luxon';
import validatePhone from 'phone';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useToast } from 'vue-toastification';
import ResponsiveDatePicker from '@app/components/admin/components/ResponsiveDatePicker.vue';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps<{
  // When opened from within a customer's profile the recipient is already known,
  // so the "who to send to" selector is hidden and this customer is pre-selected.
  customer?: ICustomer;
}>();

const emit = defineEmits(['createdVoucher', 'close']);

const adminStore = useAdminStore();
const adminClientsStore = useAdminClientsStore();
const toast = useToast();

const newVoucher = reactive<IVoucher>(<IVoucher>{
  // Default to an amount voucher so the drawer shows a usable form on open instead
  // of a blank sheet with only the type toggle.
  voucherType: VoucherTypes.AMOUNT,
  validUntilDate: DateTime.local().plus({ year: 2 }).toJSDate(),
  validFromDate: DateTime.local().toJSDate()
});
const selectedServices = ref<IService[]>([]);
const isServiceFree = ref<boolean>(true);
const whoToSend = ref<'customer' | 'phone' | 'self' | null>('customer');
const selectedCustomer = ref<ICustomer | undefined>(props.customer);
const selectedCustomerId = ref<string | undefined>(props.customer?.id);
const selectedPhone = ref<string>();
const personalMessage = ref<string>('');
const loadingCta = ref<boolean>(false);

// Locked to the customer's profile context — no recipient selection needed.
const isCustomerLocked = computed(() => !!props.customer);

const onCustomerSelected = (customer: Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'> | undefined) => {
  if (customer) {
    selectedCustomer.value = customer as ICustomer;
  }
};

const providers = computed<Array<Pick<IClient, 'userId' | 'fullName' | 'profileImage'>>>(() => adminStore.getMainClient ? adminStore.getProviders : []);
const services = computed<Array<{ label: string; items: IService[] }>>(() => {
  const allServices = adminStore.getServices.filter(service => service.visible);
  return (providers.value ?? []).map(p => ({
    label: p.fullName, items: allServices.filter(s => s.providingClients.includes(p.userId))
  })).filter(s => s.items.length > 0);
});
const validateFinished = computed(() => {
  const amountFields = newVoucher.voucherType === VoucherTypes.AMOUNT && newVoucher.amount && newVoucher.amount > 0;
  const serviceFields = newVoucher.voucherType === VoucherTypes.SERVICE && selectedServices.value.length > 0 &&
    ((newVoucher.serviceDiscount && newVoucher.serviceDiscount > 0) || isServiceFree.value === true);
  const contactIsOk = (whoToSend.value === 'customer' && !!selectedCustomer.value) || whoToSend.value === 'self' ||
    (whoToSend.value === 'phone' && !!selectedPhone.value && validatePhone(selectedPhone.value, { country: 'IL' }).isValid);

  return (amountFields || serviceFields) && contactIsOk;
});

const createVoucher = () => {
  // Guard against double-submit — without this the approve button stays active during the
  // request and a quick double-tap creates the voucher twice.
  if (loadingCta.value) return;
  trackEvent('voucher_created');
  if (!validateFinished.value) return;
  const body: IVoucher = { ...newVoucher };
  const greeting = personalMessage.value.trim();
  if (greeting) body.personalMessage = greeting;
  if (isServiceFree.value) body.serviceDiscount = 100;
  if (newVoucher.voucherType === VoucherTypes.SERVICE)
    body.serviceId = selectedServices.value.map(x => x.id);
  if (whoToSend.value === 'customer' && selectedCustomer.value)
    body.sentTo = `customer:${selectedCustomer.value.id}`;
  else if (whoToSend.value === 'phone' && selectedPhone.value)
    body.sentTo = selectedPhone.value;
  else if (whoToSend.value === 'self')
    body.sentTo = 'self';

  loadingCta.value = true;
  PromotionsApi.createVoucher(body)
    .then((res) => {
      emit('createdVoucher', res.voucherId);
      toast.success('השובר נוצר בהצלחה');
    })
    .catch(() => {
      toast.error('לא הצלחנו ליצור שובר.');
    })
    .finally(() => {
      loadingCta.value = false;
    });
};


const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
};

// Only needed to populate the recipient picker; skip when the customer is fixed.
if (!isCustomerLocked.value)
  adminClientsStore.fetchBusinessCustomers()
    .catch(e => console.error(e));
</script>

<style lang="scss">
@use '../../../../assets/css/modals' as *;

</style>
